import { AttachMoney, MoneyOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import * as MUI from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import WarehouseItemDetailsContent from "./WarehouseItemDetailsContent";
import WarehouseLabelBufferToggle from "./WarehouseLabelBufferToggle";
import WarehouseShoppingListToggle from "./WarehouseShoppingListToggle";
import { SINGLE_WAREHOUSE_ITEM } from "../../../../../../enums/UrlPaths";

export default function WarehouseItemDetailsDialog({
  item,
  currency,
  canEdit,
  canDelete,
  canViewAuditing,
  onClose,
  onEdit,
  onDelete,
  onViewAuditing,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));

  return (
    <MUI.Dialog
      open={Boolean(item)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
      data-testid="warehouse.items.details-dialog"
    >
      <MUI.DialogTitle
        sx={{ p: { xs: 2.5, sm: 3.5 }, pt: { xs: 1, sm: 1.25 } }}
      >
        <MUI.Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <MUI.Stack direction="row" spacing={0.5}>
            {canDelete && (
              <MUI.Tooltip title={t("Delete")}>
                <MUI.IconButton
                  onClick={() => onDelete(item)}
                  size="small"
                  color="error"
                  data-testid="warehouse.items.details-dialog.delete-button"
                >
                  <DeleteIcon fontSize="small" />
                </MUI.IconButton>
              </MUI.Tooltip>
            )}
            {canViewAuditing && (
              <MUI.Tooltip title={t("History")}>
                <MUI.IconButton
                  onClick={() => onViewAuditing(item)}
                  size="small"
                  data-testid="warehouse.items.details-dialog.history-button"
                >
                  <HistoryOutlinedIcon fontSize="small" />
                </MUI.IconButton>
              </MUI.Tooltip>
            )}
            {canEdit && (
              <MUI.Tooltip title={t("Edit")}>
                <MUI.IconButton
                  onClick={() => onEdit(item)}
                  size="small"
                  data-testid="warehouse.items.details-dialog.edit-button"
                >
                  <EditIcon fontSize="small" />
                </MUI.IconButton>
              </MUI.Tooltip>
            )}
            {item && (
              <WarehouseLabelBufferToggle
                item={item}
                testId="warehouse.items.details-dialog.label-queue-toggle"
              />
            )}
            {item && (
              <WarehouseShoppingListToggle
                item={item}
                testId="warehouse.items.details-dialog.shopping-list-toggle"
              />
            )}
          </MUI.Stack>

          <MUI.Stack direction="row" spacing={0.5}>
            <MUI.Tooltip title={t("ViewFullPage")}>
              <MUI.IconButton
                onClick={() =>
                  navigate(`${SINGLE_WAREHOUSE_ITEM}${item?.id}`)
                }
                size="small"
                data-testid="warehouse.items.details-dialog.view-full-page-button"
              >
                <OpenInFullIcon fontSize="small" />
              </MUI.IconButton>
            </MUI.Tooltip>
            <MUI.IconButton
              onClick={onClose}
              size="small"
              data-testid="warehouse.items.details-dialog.close-button"
            >
              <CloseIcon fontSize="small" />
            </MUI.IconButton>
          </MUI.Stack>
        </MUI.Stack>

        <MUI.Stack direction="row" spacing={2} alignItems="center">
          <MUI.Avatar
            variant="rounded"
            sx={{
              bgcolor: "primary.main",
              width: 52,
              height: 52,
              borderRadius: 2.5,
            }}
          >
            <InventoryIcon />
          </MUI.Avatar>
          <MUI.Box sx={{ minWidth: 0 }}>
            <MUI.Stack direction="row" spacing={0.75} alignItems="center">
              <MUI.Typography
                variant="h6"
                sx={{ fontWeight: 700, lineHeight: 1.25 }}
                noWrap
                data-testid="warehouse.items.details-dialog.title"
              >
                {item?.title}
              </MUI.Typography>
              <MUI.Tooltip title={t("QrCodeAvailable")}>
                <QrCode2Icon fontSize="small" color="action" />
              </MUI.Tooltip>
              <MUI.Tooltip
                title={t(item?.sellable ? "ItemIsSellable" : "ItemNoSellable")}
              >
                {item?.sellable ? (
                  <AttachMoney
                    fontSize="small"
                    color="success"
                    data-testid="warehouse.items.details-dialog.sellable-icon"
                  />
                ) : (
                  <MoneyOff
                    fontSize="small"
                    color="error"
                    data-testid="warehouse.items.details-dialog.not-sellable-icon"
                  />
                )}
              </MUI.Tooltip>
            </MUI.Stack>
            <MUI.Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5 }}
            >
              <MUI.Chip
                label={`${t("ID")}: ${item?.id}`}
                size="small"
                color="primary"
                data-testid="warehouse.items.details-dialog.id-chip"
              />
              {item?.category?.name && (
                <MUI.Chip
                  label={item.category.name}
                  size="small"
                  color="primary"
                  data-testid="warehouse.items.details-dialog.category-chip"
                />
              )}
            </MUI.Stack>
          </MUI.Box>
        </MUI.Stack>
      </MUI.DialogTitle>

      <MUI.DialogContent
        dividers
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          bgcolor: (theme) =>
            theme.palette.mode === "light"
              ? "grey.50"
              : "rgba(255,255,255,0.02)",
        }}
      >
        <WarehouseItemDetailsContent item={item} currency={currency} />
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}
